import type { google } from '@alugha/ima';
import Box from '@mui/material/Box';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { rangeInputSx, rangeMozThumbSx, rangeWebkitThumbSx } from './rangeInputSx';

type ProgressBarProps = {
  currentTime: number;
  duration: number;
  manager: google.ima.AdsManager | null;
  adContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  setCurrentTime: (newTime: number) => void;
};

export function ProgressBar({ currentTime, duration, manager, adContainerRef, setCurrentTime }: ProgressBarProps) {
  const { t } = useTranslation();
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    const video = adContainerRef.current?.querySelector('video');

    if (video) {
      video.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleSeekStart = () => {
    manager?.pause();
  };

  const handleSeekEnd = (e: React.PointerEvent<HTMLInputElement>) => {
    const time = parseFloat(e.currentTarget.value);
    const video = adContainerRef.current?.querySelector('video');

    if (video) {
      video.currentTime = time;
    }

    manager?.resume();
  };

  return (
    <Box
      component="input"
      type="range"
      value={currentTime}
      min={0}
      max={duration || 1}
      step={0.1}
      aria-label={t('Progress')}
      onChange={handleChange}
      onPointerDown={handleSeekStart}
      onPointerUp={handleSeekEnd}
      style={{ background: `linear-gradient(to right, white ${progress}%, rgba(255,255,255,0.3) ${progress}%)` }}
      sx={{
        ...rangeInputSx,
        flex: 1,
        minWidth: 0,
        mx: 1,
        '&::-webkit-slider-thumb': {
          ...rangeWebkitThumbSx,
          opacity: 0,
          transition: 'opacity 0.15s',
        },
        '&:hover::-webkit-slider-thumb': { opacity: 1 },
        '&::-moz-range-thumb': {
          ...rangeMozThumbSx,
          opacity: 0,
          transition: 'opacity 0.15s',
        },
        '&:hover::-moz-range-thumb': { opacity: 1 },
      }}
    />
  );
}
